<script setup lang="ts">
import { ref, computed } from 'vue'
import AppButton from '~/components/ui/buttons/AppButton.vue'
import { useMonitorSize } from '~/composables/monitor-size'
import type { IMonitorSize } from '~/shared/interfaces/composables'

const sizes: IMonitorSize | null = useMonitorSize()
const props = defineProps<{ additionalText: string }>()
const isExpanded = ref(false)
const contentHeight = ref<number | null>(null)
const contentRef = ref<HTMLElement | null>(null)
const initialContentHeight = !sizes?.isMobile ? 162 : 218

const toggleContent = () => {
  isExpanded.value = !isExpanded.value
}

const cleanedText = computed(() => {
  const strippedText = props.additionalText.replace(
    /<([a-z]+)[^>]*>\s*<\/\1>/gi,
    ''
  )

  if (!strippedText.trim()) {
    return ''
  }

  return props.additionalText
})

const limit = computed(() => {
  return sizes?.isMobile ? 400 : 500
})

const hasLotContent = computed(() => {
  return props.additionalText && props.additionalText.length > limit.value
})

const updateContentHeight = async () => {
  await nextTick()
  if (contentRef.value) {
    contentHeight.value = contentRef.value.scrollHeight
  }
}

onMounted(updateContentHeight)
</script>

<template>
  <div class="seo-block" v-if="cleanedText">
    <div
      ref="contentRef"
      class="seo-content"
      :class="{ 'has-gradient': hasLotContent && !isExpanded }"
      :style="{
        maxHeight: isExpanded
          ? contentHeight + 'px'
          : initialContentHeight + 'px',
      }"
    >
      <div class="seo-content__text" v-html="cleanedText"></div>
    </div>
    <div class="seo-block__button">
      <AppButton
        v-if="hasLotContent"
        @click="toggleContent"
        :type="'ghost'"
        :font-weight="400"
        :sub-type="'primary'"
        :size="'s'"
        :text="isExpanded ? 'Скрыть' : 'Показать ещё'"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.seo-block {
  padding-top: 60px;
  background-color: #ffff;
  position: relative;
  &__button {
    width: 100px;
    padding-top: 20px;
  }
}

.seo-content {
  position: relative;
  overflow: hidden;
  max-height: 100px;
  transition: max-height 0.5s ease, opacity 0.3s;

  &__text {
    color: $control-content_secondary;
    @include font(12px, 16px, 400);

    :deep(p) {
      margin-bottom: 8px;
    }

    :deep(a) {
      display: inline;
      color: $control-bg_primary;
      @include font(12px, 16px, 400);

      &:hover {
        color: $control-bg_primary-hover;
      }
    }

    :deep(h2), :deep(h3), :deep(h4) {
      margin-bottom: 28px;
      color: $text-primary;
      @include font(16px, 20px, 500);

      @include sm-breakpoint {
        margin-bottom: 24px;
      }
    }

    :deep(ul) {
      list-style: none;
      padding: 0;
      p {
        margin-bottom: 0;
      }
    }
    :deep(li) {
      position: relative;
      margin-bottom: 12px;
      padding-left: 24px;

      &::before {
        content: '';
        position: absolute;
        left: 0px;
        top: 7px;
        width: 16px;
        height: 2px;
        background: $control-bg_primary;
      }
    }
  }

  &.expanded::after {
    opacity: 0;
  }

  &.has-gradient::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 60px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      #fff 100%
    );
    pointer-events: none;
    opacity: 1;
    transition: opacity 0.3s;
  }
}
</style>
